import { reactive } from 'vue'
import { jwt, hasAdminRole, hasRole } from '@/utils/jwt.js'
import roles from '@/utils/roles'

function isImplicitBetaUser() {
    return jwt.organizationId == 1 && hasRole(roles.featureEditor)
}

function isAdminUser() {
    return hasAdminRole()
}

// default state; this should generally represent what we want to be happening in production
const defaults = Object.freeze({
    dataDogEnabled: {
        editable: false,
        name: 'DataDog Enabled',
        server: false,
        value: () => false,
    },
    environment: {
        editable: false,
        name: 'Environment',
        server: false,
        value: () => '',
    },
    incentiveAnalysisEnabled: {
        editable: true,
        name: 'Show Incentive Analysis instead of ISS Incentives data on Executive Analysis tab',
        server: true,
        value: isImplicitBetaUser,
    },
})

export const DEFAULTS = Object.entries(defaults).map(([key, value]) => ({ key, ...value }))

function getDefaults() {
    const result = {}
    Object.entries(defaults).forEach(([key, value]) => (result[key] = value.value()))
    return result
}

// use the hostname to determine the configuration to use
const configurations = {
    'managementtrack.com': {
        dataDogEnabled: true,
        environment: 'production',
    },
    'mt-staging.net': {
        dataDogEnabled: true,
        environment: 'staging',
    },
    localhost: {
        dataDogEnabled: false,
        environment: 'development',
    },
}

function getEnvironment(hostname) {
    while (true) {
        const configuration = configurations[hostname]

        if (configuration) {
            return configuration
        }

        const index = hostname.indexOf('.')

        if (index < 0) {
            break
        }

        hostname = hostname.substring(index + 1)
    }

    return configurations.localhost
}

let localStorage = {}

function persist() {
    if (Object.keys(localStorage).length > 0) {
        window.localStorage.setItem('features', JSON.stringify(localStorage))
    } else {
        window.localStorage.removeItem('features')
    }
}

export const features = reactive({
    ...getDefaults(),
    ...getEnvironment(document.location.hostname),
    ...localStorage,
})

function refresh() {
    try {
        localStorage = JSON.parse(window.localStorage.getItem('features')) || {}
    } catch {
        localStorage = {}
        persist()
    }

    Object.assign(features, getDefaults(), getEnvironment(document.location.hostname), localStorage)
}

function editable(key) {
    return defaults[key]?.editable ?? false
}

export function set(key, value) {
    if (editable(key)) {
        localStorage[key] = value
        persist()
        refresh()
    }
}

refresh()

window.addEventListener('storage', e => {
    if (e.key === 'features') {
        refresh()
    }
})
